// corner-overlay.js - 실시간 코너 오버레이 렌더링

import { StabilityState } from './stability-tracker.js';

/**
 * 상태별 색상
 */
const STATE_COLORS = {
  [StabilityState.NOT_DETECTED]: { stroke: '#ef4444', fill: 'rgba(239, 68, 68, 0.1)' },  // 빨간색
  [StabilityState.UNSTABLE]: { stroke: '#eab308', fill: 'rgba(234, 179, 8, 0.1)' },       // 노란색
  [StabilityState.STABILIZING]: { stroke: '#22c55e', fill: 'rgba(34, 197, 94, 0.15)' },   // 초록색 (연함)
  [StabilityState.STABLE]: { stroke: '#22c55e', fill: 'rgba(34, 197, 94, 0.25)' }         // 초록색 (진함)
};

/**
 * 코너 오버레이 렌더러
 */
export class CornerOverlay {
  constructor(canvas, videoElement) {
    if (!canvas) throw new Error('CornerOverlay: canvas element is required');
    this.canvas = canvas;
    this.video = videoElement;
    this.ctx = canvas.getContext('2d', { alpha: true });

    // 스무딩용 이전 코너 (더 빠른 반응)
    this.prevCorners = null;
    this.smoothingFactor = 0.5; // 0~1, 높을수록 더 즉각적

    // 애니메이션 상태
    this.pulsePhase = 0;

    // 캐시된 변환 값
    this.cachedTransform = null;
    this.lastVideoSize = { w: 0, h: 0 };

    // 부드러운 진행률 애니메이션
    this.displayedProgress = 0;
  }

  /**
   * 캔버스 크기를 비디오에 맞춤
   */
  resize() {
    const rect = this.video.getBoundingClientRect();
    if (this.canvas.width !== rect.width || this.canvas.height !== rect.height) {
      this.canvas.width = rect.width;
      this.canvas.height = rect.height;
    }
    this.updateTransformCache();
  }

  /**
   * 변환 파라미터 계산 (캐싱)
   */
  updateTransformCache() {
    const videoWidth = this.video.videoWidth;
    const videoHeight = this.video.videoHeight;
    const canvasWidth = this.canvas.width;
    const canvasHeight = this.canvas.height;

    // 크기 변경 없으면 캐시 사용
    if (this.cachedTransform &&
        this.lastVideoSize.w === videoWidth &&
        this.lastVideoSize.h === videoHeight &&
        this.lastVideoSize.cw === canvasWidth &&
        this.lastVideoSize.ch === canvasHeight) {
      return;
    }

    this.lastVideoSize = { w: videoWidth, h: videoHeight, cw: canvasWidth, ch: canvasHeight };

    if (!videoWidth || !videoHeight) {
      this.cachedTransform = null;
      return;
    }

    // object-fit: contain 계산
    const videoRatio = videoWidth / videoHeight;
    const canvasRatio = canvasWidth / canvasHeight;

    let renderedWidth, renderedHeight, offsetX, offsetY;

    if (videoRatio > canvasRatio) {
      renderedWidth = canvasWidth;
      renderedHeight = canvasWidth / videoRatio;
      offsetX = 0;
      offsetY = (canvasHeight - renderedHeight) / 2;
    } else {
      renderedHeight = canvasHeight;
      renderedWidth = canvasHeight * videoRatio;
      offsetX = (canvasWidth - renderedWidth) / 2;
      offsetY = 0;
    }

    this.cachedTransform = {
      scaleX: renderedWidth / videoWidth,
      scaleY: renderedHeight / videoHeight,
      offsetX,
      offsetY
    };
  }

  /**
   * 비디오 좌표를 캔버스 좌표로 변환
   * (object-fit: contain 레터박스 고려)
   */
  videoToCanvas(point) {
    if (!this.cachedTransform) return point;

    const { scaleX, scaleY, offsetX, offsetY } = this.cachedTransform;
    return {
      x: point.x * scaleX + offsetX,
      y: point.y * scaleY + offsetY
    };
  }

  /**
   * 코너 스무딩
   */
  smoothCorners(newCorners) {
    if (!this.prevCorners) {
      this.prevCorners = newCorners;
      return newCorners;
    }

    const smoothed = {};
    const keys = ['tl', 'tr', 'br', 'bl'];

    for (const key of keys) {
      smoothed[key] = {
        x: this.prevCorners[key].x + (newCorners[key].x - this.prevCorners[key].x) * this.smoothingFactor,
        y: this.prevCorners[key].y + (newCorners[key].y - this.prevCorners[key].y) * this.smoothingFactor
      };
    }

    this.prevCorners = smoothed;
    return smoothed;
  }

  /**
   * 오버레이 렌더링
   * @param {Object|null} corners - 비디오 좌표계의 코너
   * @param {string} state - StabilityState
   * @param {number} progress - 안정화 진행률 (0~1)
   */
  render(corners, state, progress = 0) {
    this.resize();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // 펄스 애니메이션 업데이트
    this.pulsePhase = (this.pulsePhase + 0.1) % (Math.PI * 2);

    if (!corners || state === StabilityState.NOT_DETECTED) {
      this.prevCorners = null;
      // 문서 미감지 시 가이드 표시만
      this.renderGuide();
      return;
    }

    // 코너 스무딩
    const smoothed = this.smoothCorners(corners);

    // 캔버스 좌표로 변환
    const canvasCorners = {
      tl: this.videoToCanvas(smoothed.tl),
      tr: this.videoToCanvas(smoothed.tr),
      br: this.videoToCanvas(smoothed.br),
      bl: this.videoToCanvas(smoothed.bl)
    };

    const colors = STATE_COLORS[state];

    // 문서 영역 채우기
    this.renderFill(canvasCorners, colors.fill);

    // 테두리 그리기
    this.renderOutline(canvasCorners, colors.stroke, state);

    // 코너 점 그리기
    this.renderCornerDots(canvasCorners, colors.stroke, state);

    // 안정화 진행 상태일 때 진행률 표시 (부드럽게 보간)
    if (state === StabilityState.STABILIZING || state === StabilityState.STABLE) {
      // 진행률 부드럽게 증가 (감소는 빠르게)
      if (progress > this.displayedProgress) {
        this.displayedProgress += (progress - this.displayedProgress) * 0.3;
      } else {
        this.displayedProgress = progress; // 감소는 즉시
      }
      this.renderProgressIndicator(canvasCorners, this.displayedProgress, state);
    } else {
      this.displayedProgress = 0;
    }
  }

  /**
   * 문서 영역 채우기
   */
  renderFill(corners, fillColor) {
    const ctx = this.ctx;

    ctx.beginPath();
    ctx.moveTo(corners.tl.x, corners.tl.y);
    ctx.lineTo(corners.tr.x, corners.tr.y);
    ctx.lineTo(corners.br.x, corners.br.y);
    ctx.lineTo(corners.bl.x, corners.bl.y);
    ctx.closePath();

    ctx.fillStyle = fillColor;
    ctx.fill();
  }

  /**
   * 테두리 그리기
   */
  renderOutline(corners, strokeColor, state) {
    const ctx = this.ctx;

    ctx.beginPath();
    ctx.moveTo(corners.tl.x, corners.tl.y);
    ctx.lineTo(corners.tr.x, corners.tr.y);
    ctx.lineTo(corners.br.x, corners.br.y);
    ctx.lineTo(corners.bl.x, corners.bl.y);
    ctx.closePath();

    // 안정 상태에서 펄스 효과
    let lineWidth = 3;
    if (state === StabilityState.STABLE) {
      lineWidth = 3 + Math.sin(this.pulsePhase) * 1.5;
    }

    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
  }

  /**
   * 코너 점 그리기
   */
  renderCornerDots(corners, strokeColor, state) {
    const ctx = this.ctx;
    const keys = ['tl', 'tr', 'br', 'bl'];

    // 안정 상태에서 점 크기 펄스
    let radius = 8;
    if (state === StabilityState.STABLE) {
      radius = 8 + Math.sin(this.pulsePhase) * 2;
    }

    for (const key of keys) {
      const p = corners[key];

      // 외곽 원
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = strokeColor;
      ctx.fill();

      // 내부 원 (흰색)
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = 'white';
      ctx.fill();
    }
  }

  /**
   * 진행률 표시 (원형 프로그레스)
   */
  renderProgressIndicator(corners, progress, state) {
    if (state === StabilityState.STABLE) return; // 완료 시 숨김

    const ctx = this.ctx;

    // 중심점 계산
    const centerX = (corners.tl.x + corners.tr.x + corners.br.x + corners.bl.x) / 4;
    const centerY = (corners.tl.y + corners.tr.y + corners.br.y + corners.bl.y) / 4;

    const radius = 30;

    // 배경 원
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fill();

    // 진행률 호
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius - 5, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress);
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    ctx.stroke();

    // 체크 아이콘 (progress > 80%)
    if (progress > 0.8) {
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(centerX - 8, centerY);
      ctx.lineTo(centerX - 2, centerY + 6);
      ctx.lineTo(centerX + 10, centerY - 6);
      ctx.stroke();
    }
  }

  /**
   * 문서 미감지 시 가이드 표시
   */
  renderGuide() {
    // 가이드 메시지는 HTML로 처리 (성능상 이점)
    // 필요시 여기서 캔버스에 텍스트 렌더링 가능
  }

  /**
   * 캡처 플래시 효과
   */
  flashCapture() {
    const ctx = this.ctx;

    // 초록색 테두리 플래시
    ctx.save();
    ctx.strokeStyle = '#22c55e';
    ctx.lineWidth = 8;
    ctx.strokeRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.restore();

    // 0.15초 후 클리어
    setTimeout(() => {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }, 150);
  }

  /**
   * 리셋
   */
  reset() {
    this.prevCorners = null;
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}
